import { supabase } from '@/integrations/supabase/client';
import { generateReferralCode, ReferralCodeRow } from '@/lib/referral-shared';
import { logError } from '@/lib/sentry';
import { getEdgeFunctionError } from '@/lib/utils';

export type ReferralStatus = 'clicked' | 'account_created' | 'accepted_to_campaign' | 'paid';

export interface ReferralListItem {
  id: string;
  referee_id: string | null;
  referee_display: string;
  status: ReferralStatus;
  created_at: string;
  converted_at: string | null;
  paid_at: string | null;
  amount: number;
  notes?: string | null;
}

export interface ReferralBudgetSummary {
  month_key: string;
  total_budget: number;
  spent: number;
  remaining: number;
  percent_spent: number;
}

export interface ReferralDashboardData {
  enabled: boolean;
  creator_allowed: boolean;
  referral_url: string;
  current_month: string;
  monthly_limit: number;
  reward_amount: number;
  show_budget_to_creators: boolean;
  this_month: {
    successful_referrals: number;
    pending_referrals: number;
    earnings: number;
  };
  lifetime: {
    successful_referrals: number;
    earnings: number;
  };
  budget: ReferralBudgetSummary;
  referrals: ReferralListItem[];
}

export type ReferralVisibilityState =
  | 'hidden'
  | 'visible_allowed'
  | 'visible_open_mode'
  | 'visible_paused';

export interface ReferralAccessState {
  state: ReferralVisibilityState;
  creator_allowed: boolean;
  has_active_code: boolean;
  has_explicit_pause: boolean;
  enabled: boolean;
  beta_mode: boolean;
  reward_amount: number;
  monthly_limit: number;
  show_budget_to_creators: boolean;
}

type ReferralSettings = {
  enabled: boolean;
  reward_amount: number;
  monthly_cap: number;
  default_monthly_budget: number;
  show_budget_to_creators: boolean;
  enforce_global_budget: boolean;
  beta_mode: boolean;
};

const getMonthKey = (date = new Date()): string => {
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  return utc.toISOString().slice(0, 10);
};

const maskEmail = (email: string): string => {
  const [name, domain] = email.split('@');
  if (!domain) return email;
  if (name.length <= 2) {
    return `${name[0] || '*'}*@${domain}`;
  }
  return `${name.slice(0, 2)}${'*'.repeat(Math.max(1, name.length - 2))}@${domain}`;
};

export class ReferralService {
  private static settingsCache: { value: ReferralSettings; loadedAt: number } | null = null;

  static async getExistingCode(referrerId: string): Promise<ReferralCodeRow | null> {
    const { data, error } = await supabase
      .from('referral_codes')
      .select('code, reward_amount, monthly_cap, active')
      .eq('referrer_id', referrerId)
      .maybeSingle();

    if (error) {
      logError('Failed to load referral code', error);
      return null;
    }

    return data as ReferralCodeRow | null;
  }

  static async getAccessState(referrerId: string): Promise<ReferralAccessState> {
    const settings = await ReferralService.getSettings();
    const referralCode = await ReferralService.getExistingCode(referrerId);
    const hasExplicitPause = referralCode?.active === false;
    const hasActiveCode = Boolean(referralCode?.code && referralCode.active);
    const creatorAllowed = settings.beta_mode ? hasActiveCode : !hasExplicitPause;

    if (!creatorAllowed) {
      return {
        state: 'hidden',
        creator_allowed: false,
        has_active_code: hasActiveCode,
        has_explicit_pause: hasExplicitPause,
        enabled: settings.enabled,
        beta_mode: settings.beta_mode,
        reward_amount: Number(referralCode?.reward_amount ?? settings.reward_amount),
        monthly_limit: Number(referralCode?.monthly_cap ?? settings.monthly_cap),
        show_budget_to_creators: settings.show_budget_to_creators,
      };
    }

    if (!settings.enabled) {
      return {
        state: 'visible_paused',
        creator_allowed: true,
        has_active_code: hasActiveCode,
        has_explicit_pause: hasExplicitPause,
        enabled: settings.enabled,
        beta_mode: settings.beta_mode,
        reward_amount: Number(referralCode?.reward_amount ?? settings.reward_amount),
        monthly_limit: Number(referralCode?.monthly_cap ?? settings.monthly_cap),
        show_budget_to_creators: settings.show_budget_to_creators,
      };
    }

    return {
      state: settings.beta_mode ? 'visible_allowed' : 'visible_open_mode',
      creator_allowed: true,
      has_active_code: hasActiveCode,
      has_explicit_pause: hasExplicitPause,
      enabled: settings.enabled,
      beta_mode: settings.beta_mode,
      reward_amount: Number(referralCode?.reward_amount ?? settings.reward_amount),
      monthly_limit: Number(referralCode?.monthly_cap ?? settings.monthly_cap),
      show_budget_to_creators: settings.show_budget_to_creators,
    };
  }

  static async getOrCreateCode(referrerId: string, allowCreate: boolean): Promise<ReferralCodeRow | null> {
    const existing = await ReferralService.getExistingCode(referrerId);

    if (existing?.code && existing.active) {
      return existing;
    }

    if (!allowCreate) {
      return null;
    }

    if (existing && existing.active === false) {
      return null;
    }

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const code = generateReferralCode();
      const { data, error } = await supabase
        .from('referral_codes')
        .insert({ referrer_id: referrerId, code })
        .select('code, reward_amount, monthly_cap, active')
        .single();

      if (!error && data?.code) {
        return data as ReferralCodeRow;
      }

      if (error?.code === '23505') {
        // Either code or referrer already exists, retry or fetch existing.
        const { data: retryExisting } = await supabase
          .from('referral_codes')
          .select('code, reward_amount, monthly_cap, active')
          .eq('referrer_id', referrerId)
          .maybeSingle();
        if (retryExisting?.code && retryExisting.active) {
          return retryExisting as ReferralCodeRow;
        }
      }
    }

    throw new Error('Unable to generate referral code');
  }

  static async recordSignup(code: string): Promise<{ success: boolean; reason?: string }> {
    const { data, error } = await supabase
      .rpc('record_referral_signup', { p_code: code });

    if (error) {
      logError('Failed to record referral signup', error);
      return { success: false, reason: error.message };
    }

    if (typeof data === 'object' && data && 'success' in data) {
      return { success: Boolean((data as any).success), reason: (data as any).reason };
    }

    return { success: true };
  }

  static async handleCampaignAcceptance(creatorId: string, campaignId: string): Promise<void> {
    try {
      const settings = await ReferralService.getSettings();
      if (!settings.enabled) {
        return;
      }
      const response = await supabase.functions.invoke('referral-acceptance', {
        body: {
          creatorId,
          campaignId,
          environment: import.meta.env.MODE === 'development' ? 'development' : 'production',
        },
      });

      if (response.error || response.data?.success === false) {
        const errorMessage = await getEdgeFunctionError(response, 'Failed to process referral payout');
        logError('Referral acceptance edge function error', errorMessage);
      }
    } catch (error) {
      logError('Referral acceptance failed', error);
    }
  }

  static async getDashboardData(referrerId: string): Promise<ReferralDashboardData> {
    const monthKey = getMonthKey();

    const settings = await ReferralService.getSettings();
    const referralCode = await ReferralService.getOrCreateCode(referrerId, !settings.beta_mode);
    const creatorAllowed = Boolean(referralCode?.code);

    if (!creatorAllowed) {
      return {
        enabled: settings.enabled,
        creator_allowed: false,
        referral_url: '',
        current_month: monthKey,
        monthly_limit: settings.monthly_cap,
        reward_amount: settings.reward_amount,
        show_budget_to_creators: settings.show_budget_to_creators,
        this_month: {
          successful_referrals: 0,
          pending_referrals: 0,
          earnings: 0,
        },
        lifetime: {
          successful_referrals: 0,
          earnings: 0,
        },
        budget: {
          month_key: monthKey,
          total_budget: 0,
          spent: 0,
          remaining: 0,
          percent_spent: 0,
        },
        referrals: [],
      };
    }

    const [budget, referrals] = await Promise.all([
      ReferralService.getBudget(monthKey),
      ReferralService.getReferrals(referrerId),
    ]);

    const referralUrl = `${window.location.origin}/auth?tab=signup&ref=${referralCode?.code ?? ''}`;

    const thisMonthReferrals = referrals.filter((ref) => ref.month_key === monthKey);

    const successfulThisMonth = thisMonthReferrals.filter((ref) =>
      ref.status === 'accepted_to_campaign' || ref.status === 'paid'
    );

    const pendingThisMonth = thisMonthReferrals.filter((ref) => ref.status === 'account_created');

    const monthlyEarnings = thisMonthReferrals
      .filter((ref) => ref.status === 'paid')
      .reduce((sum, ref) => sum + ref.amount, 0);

    const lifetimeSuccessful = referrals.filter((ref) =>
      ref.status === 'accepted_to_campaign' || ref.status === 'paid'
    );

    const lifetimeEarnings = referrals
      .filter((ref) => ref.status === 'paid')
      .reduce((sum, ref) => sum + ref.amount, 0);

    const formattedReferrals: ReferralListItem[] = referrals.map((ref) => ({
      id: ref.id,
      referee_id: ref.referee_id,
      referee_display: ref.referee_display,
      status: ref.status,
      created_at: ref.created_at,
      converted_at: ref.converted_at,
      paid_at: ref.paid_at,
      amount: ref.amount,
      notes: ref.notes,
    }));

    return {
      enabled: settings.enabled,
      creator_allowed: creatorAllowed,
      referral_url: referralUrl,
      current_month: monthKey,
      monthly_limit: Number(referralCode?.monthly_cap ?? settings.monthly_cap),
      reward_amount: Number(referralCode?.reward_amount ?? settings.reward_amount),
      show_budget_to_creators: settings.show_budget_to_creators,
      this_month: {
        successful_referrals: successfulThisMonth.length,
        pending_referrals: pendingThisMonth.length,
        earnings: monthlyEarnings,
      },
      lifetime: {
        successful_referrals: lifetimeSuccessful.length,
        earnings: lifetimeEarnings,
      },
      budget,
      referrals: formattedReferrals,
    };
  }

  private static async getSettings(): Promise<ReferralSettings> {
    const now = Date.now();
    if (ReferralService.settingsCache && now - ReferralService.settingsCache.loadedAt < 60_000) {
      return ReferralService.settingsCache.value;
    }

    const { data, error } = await supabase
      .from('referral_settings')
      .select('enabled, reward_amount, monthly_cap, default_monthly_budget, show_budget_to_creators, enforce_global_budget, beta_mode')
      .maybeSingle();

    if (error) {
      logError('Failed to load referral settings', error);
    }

    const settings: ReferralSettings = {
      enabled: data?.enabled ?? true,
      reward_amount: Number(data?.reward_amount ?? 5),
      monthly_cap: Number(data?.monthly_cap ?? 5),
      default_monthly_budget: Number(data?.default_monthly_budget ?? 0),
      show_budget_to_creators: data?.show_budget_to_creators ?? false,
      enforce_global_budget: data?.enforce_global_budget ?? true,
      beta_mode: data?.beta_mode ?? false,
    };

    ReferralService.settingsCache = { value: settings, loadedAt: now };
    return settings;
  }

  private static async getBudget(monthKey: string): Promise<ReferralBudgetSummary> {
    const { data, error } = await supabase
      .rpc('get_or_create_referral_budget', { p_month: monthKey })
      .returns<{ month_key: string; total_budget: number; spent: number }[]>();

    if (error) {
      logError('Failed to load referral budget', error);
    }

    const budgetRow = data?.[0];
    const totalBudget = Number(budgetRow?.total_budget ?? 0);
    const spent = Number(budgetRow?.spent ?? 0);
    const remaining = Math.max(0, totalBudget - spent);
    const percentSpent = totalBudget > 0 ? spent / totalBudget : 0;

    return {
      month_key: budgetRow?.month_key ?? monthKey,
      total_budget: totalBudget,
      spent,
      remaining,
      percent_spent: percentSpent,
    };
  }

  private static async getReferrals(referrerId: string): Promise<Array<{
    id: string;
    referee_id: string | null;
    referee_display: string;
    status: ReferralStatus;
    created_at: string;
    converted_at: string | null;
    paid_at: string | null;
    amount: number;
    month_key: string;
    notes: string | null;
  }>> {
    const { data, error } = await supabase
      .from('referrals')
      .select('id, referee_id, status, created_at, converted_at, paid_at, amount, month_key, notes')
      .eq('referrer_id', referrerId)
      .order('created_at', { ascending: false });

    if (error) {
      logError('Failed to load referrals', error);
      return [];
    }

    const referrals = data ?? [];
    const refereeIds = referrals
      .map((ref) => ref.referee_id)
      .filter((id): id is string => Boolean(id));

    const { data: referees } = refereeIds.length
      ? await supabase
        .from('users')
        .select('id, full_name, email')
        .in('id', refereeIds)
      : { data: [] };

    const refereeMap = new Map<string, { full_name: string | null; email: string | null }>();
    (referees ?? []).forEach((referee) => {
      refereeMap.set(referee.id, { full_name: referee.full_name, email: referee.email });
    });

    return referrals.map((referral) => {
      const referee = referral.referee_id ? refereeMap.get(referral.referee_id) : null;
      const displayName = referee?.full_name || (referee?.email ? maskEmail(referee.email) : 'Pending signup');

      return {
        ...referral,
        referee_display: displayName,
      };
    });
  }
}
